/**
 * Worklist assignments: who was assigned to which worklist, for which facilities, and
 * when (reviews #405, #406, Shayna 2026-10-12: "I need this page to be on this report…
 * if we click on the predicted worklist it will take us here to show that the user was
 * assigned to the worklist at that time").
 *
 * The VP Labor Tool's "Worklist Data (Raw)" page, which lists one row per day ×
 * person × facility × worklist. Here a row is an unbroken run of those days, From and
 * To, so a person on a list all summer is one row instead of ninety. The timeline
 * above it draws the same runs, one lane per person on each worklist, so two people
 * on one list, or two lists on one facility, show side by side.
 *
 * Read from assignments.json (src/data/assignments.ts), scoped per person by the API:
 * a caller sees the assignments of the people they may see and nobody else's. The
 * worklist and the month are in the URL (Claim detail links here with both); the
 * person is kept in the history entry and goes into a shared link as an opaque key,
 * never a name (state/personKeys.ts, review #156).
 */
import { useMemo, useState, useEffect } from 'react'
import { useAppData } from '@/data/AppData'
import { applyFilters, useFilters } from '@/data/filters'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { useClaimDetail } from '@/data/claimDetail'
import { facilityGroupById, facilityGroupOf, inGroups } from '@/data/facilityGroups'
import {
  daysIn,
  decodeAssignments,
  monthBounds,
  monthsBetween,
  overlapsDays,
  overlapsOf,
  priorityOn,
  prioritiesIn,
  priorityText,
  useAssignments,
  worklistNames,
  type AssignmentRow,
} from '@/data/assignments'
import { DataTable, type Column } from '@/components/Table'
import { SearchPick } from '@/components/SearchPick'
import { ExportBar } from '@/components/ExportBar'
import { PopOut } from '@/components/PopOut'
import { useUrlState, text, bool } from '@/ui/urlState'
import { int, monthYear } from '@/format'
import type { ExportColumn } from '@/ui/export'

/** "2026-07-01" -> "7/1/2026", as Claim detail shows dates. */
const usDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(v) ? `${+v.slice(5, 7)}/${+v.slice(8, 10)}/${v.slice(0, 4)}` : v

/** The timeline draws at most this many lanes; the table below has every row. */
const MAX_LANES = 60

const control: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 7px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
}

interface Lane {
  worklistId: number
  worklist: string
  /** At the window's last day where the history has it, else today's. */
  priority: number | null
  /** Whether `priority` is from the history (false: today's, the history not covering it). */
  dated: boolean
  system: boolean
  person: string
  /** Merged spans of days, clipped to the window. */
  spans: { from: string; to: string }[]
  facilities: number
  /** Someone else was on this same worklist on some of the same days, in the window. */
  shared: boolean
}

/** Runs → one lane per worklist × person, its days merged across facilities. */
function lanesOf(
  rows: readonly AssignmentRow[],
  overlaps: ReturnType<typeof overlapsOf>,
  from: string,
  to: string,
): Lane[] {
  const byLane = new Map<string, { lane: Lane; runs: AssignmentRow[] }>()
  for (const r of rows) {
    const k = `${r.worklistId}␟${r.person}`
    let e = byLane.get(k)
    if (!e) {
      e = {
        lane: {
          worklistId: r.worklistId,
          worklist: r.worklist,
          priority: priorityOn(r.priorities, to) ?? r.priority,
          dated: priorityOn(r.priorities, to) !== null,
          system: r.system,
          person: r.person,
          spans: [],
          facilities: 0,
          shared: false,
        },
        runs: [],
      }
      byLane.set(k, e)
    }
    e.runs.push(r)
    if (overlaps.get(r)?.shared.some((x) => overlapsDays(x, from, to))) e.lane.shared = true
  }
  const next = (d: string) => new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)
  return [...byLane.values()]
    .map(({ lane, runs }) => {
      lane.facilities = new Set(runs.map((r) => r.facility)).size
      const sorted = runs
        .map((r) => ({ from: r.from < from ? from : r.from, to: r.to > to ? to : r.to }))
        .filter((s) => s.from <= s.to)
        .sort((a, b) => a.from.localeCompare(b.from))
      for (const s of sorted) {
        const last = lane.spans[lane.spans.length - 1]
        if (last && s.from <= next(last.to)) {
          if (s.to > last.to) last.to = s.to
        } else lane.spans.push({ ...s })
      }
      return lane
    })
    .sort((a, b) => a.worklist.localeCompare(b.worklist) || a.person.localeCompare(b.person))
}

/** A run that reaches the data's last day: still assigned then, and maybe still. */
function Ongoing({ last }: { last: string }) {
  const says = `Still assigned on ${usDate(last)}, the last day of the data: the run may be going on.`
  return (
    <span className="wa-ongoing" title={says} data-testid="assignment-ongoing">
      → ongoing
    </span>
  )
}

function Timeline({ lanes, from, to, total }: { lanes: Lane[]; from: string; to: string; total: number }) {
  const span = daysIn(from, to)
  const at = (d: string) => ((daysIn(from, d) - 1) / span) * 100
  const months = monthsBetween(from, to)
  let lastWorklist: number | null = null
  return (
    <div className="wa-timeline" data-testid="assignment-timeline">
      <div className="wa-axis">
        <div className="wa-label" />
        <div className="wa-track">
          {months.map((m) => {
            const b = monthBounds(m)
            const left = Math.max(0, at(b.from < from ? from : b.from))
            return (
              <span key={m} className="wa-tick" style={{ left: `${left}%` }}>
                {monthYear(m)}
              </span>
            )
          })}
        </div>
      </div>
      {lanes.map((l) => {
        const first = l.worklistId !== lastWorklist
        lastWorklist = l.worklistId
        return (
          <div key={`${l.worklistId}:${l.person}`} className={`wa-lane${first ? ' wa-first' : ''}`}>
            <div className="wa-label">
              {first && (
                <div className="wa-wl" title={l.worklist}>
                  {l.worklist}
                  {l.priority !== null && (
                    <span className="wa-pr" title={l.dated ? `On ${usDate(to)}` : 'Today’s: the priority history does not reach this day'}>
                      {' '}
                      · priority {int(l.priority)}
                      {l.dated ? '' : ' (today)'}
                    </span>
                  )}
                  {l.system && <span className="wa-pr"> · system list</span>}
                </div>
              )}
              <div className="wa-person">
                {l.person}
                {l.shared && (
                  <span
                    className="wa-flag"
                    title="Someone else was on this same worklist on some of the same days. When the model credits a claim on a list with two people, it takes the first by email address."
                  >
                    {' '}
                    ⚑
                  </span>
                )}
              </div>
            </div>
            <div className="wa-track">
              {months.map((m) => (
                <span key={m} className="wa-grid" style={{ left: `${at(monthBounds(m).from < from ? from : monthBounds(m).from)}%` }} />
              ))}
              {l.spans.map((s) => (
                <span
                  key={s.from}
                  className={`wa-bar${l.system ? ' wa-sys' : ''}`}
                  style={{ left: `${at(s.from)}%`, width: `${Math.max(0.6, (daysIn(s.from, s.to) / span) * 100)}%` }}
                  title={`${l.person} on ${l.worklist}: ${usDate(s.from)} – ${usDate(s.to)} (${int(daysIn(s.from, s.to))} days, ${int(l.facilities)} ${l.facilities === 1 ? 'facility' : 'facilities'})`}
                />
              ))}
            </div>
          </div>
        )
      })}
      {total > lanes.length && (
        <p className="body-note" style={{ marginTop: 8 }}>
          The first {int(lanes.length)} of {int(total)} lanes. Pick a worklist, person or
          facility to see the rest; the table below has every row.
        </p>
      )}
    </div>
  )
}

export function WorklistAssignments() {
  const { derived } = useAppData()
  const { filters } = useFilters()
  const dataset = useLoadedDataset()
  const { data, error } = useAssignments()

  // In the URL: names nobody (an id, a facility, a month). Claim detail links here
  // with ?worklist=…&month=…
  const [worklist, setWorklist] = useUrlState('worklist', '', text)
  const [month, setMonth] = useUrlState('month', '', text)
  const [facility, setFacility] = useUrlState('facility', '', text)
  const [system, setSystem] = useUrlState('system', false, bool)
  // A person: in the history entry, and as an opaque key in a shared link.
  const [person, setPerson] = useUrlState('person', '', text, { personal: true })
  const [q, setQ] = useUrlState('q', '', text, { personal: true, share: false })
  const [typed, setTyped] = useState(q)
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim().toLowerCase()), 250)
    return () => clearTimeout(t)
  }, [typed])

  // The top bar's person filters, as on Claim detail: who is on the page at all. Its
  // Facility group filter selects the rows themselves, below.
  const people = useMemo(
    () =>
      new Set(
        applyFilters(derived, dataset.composition, {
          ...filters,
          claimType: [],
          serviceType: [],
          claimStatus: [],
          facilityGroup: [],
        }).people.map((p) => p.employee),
      ),
    [derived, dataset.composition, filters],
  )

  /*
   * The top bar's Facility group filter, by each run's facility, exactly. An assignment
   * names its facility as Medevolve's configuration does, not as the claims do, so it is
   * placed in a group by its Medevolve id through Claim detail's rows
   * (`facilityGroupById`), which load only while the filter is on. A facility no claim
   * you can see carries has no known group: its runs are left out and counted.
   */
  const groups = filters.facilityGroup ?? []
  const claimRows = useClaimDetail(groups.length > 0)
  const groupById = useMemo(
    () =>
      groups.length && claimRows.data
        ? facilityGroupById(claimRows.data, facilityGroupOf(dataset.crosstab))
        : null,
    [groups.length, claimRows.data, dataset.crosstab],
  )
  /** 'off', 'waiting' for the claim rows, 'no-ids' on a build without them, or 'on'. */
  const placing: 'off' | 'waiting' | 'no-ids' | 'on' = !groups.length
    ? 'off'
    : !claimRows.data
      ? claimRows.error
        ? 'no-ids'
        : 'waiting'
      : groupById === null
        ? 'no-ids'
        : 'on'

  const all = useMemo(() => (data ? decodeAssignments(data) : []), [data])
  // An assignments file built before the facility ids can place nothing.
  const groupState =
    placing === 'on' && all.length > 0 && all.every((r) => r.facilityId === null) ? 'no-ids' : placing
  const names = useMemo(() => worklistNames(all), [all])
  const months = useMemo(() => monthsBetween(data?.from ?? null, data?.to ?? null), [data])
  const window_ = useMemo(() => {
    if (month) return monthBounds(month)
    return { from: data?.from ?? '', to: data?.to ?? '' }
  }, [month, data])

  const { rows, hidden, unplaced } = useMemo(() => {
    const out: AssignmentRow[] = []
    let hidden = 0
    let unplaced = 0
    for (const r of all) {
      if (!system && r.system) continue
      if (worklist && String(r.worklistId) !== worklist) continue
      if (facility && r.facility !== facility) continue
      if (groupState === 'on') {
        const g = r.facilityId === null ? undefined : groupById!.get(r.facilityId)
        if (g === undefined) {
          unplaced++
          continue
        }
        if (!inGroups(g, groups)) continue
      }
      if (person && r.person !== person) continue
      if (month && !overlapsDays(r, window_.from, window_.to)) continue
      if (
        q &&
        !r.worklist.toLowerCase().includes(q) &&
        !r.person.toLowerCase().includes(q) &&
        !r.facility.toLowerCase().includes(q)
      )
        continue
      if (!people.has(r.person)) {
        hidden++
        continue
      }
      out.push(r)
    }
    return { rows: out, hidden, unplaced }
  }, [all, system, worklist, facility, person, month, window_, q, people, groupState, groupById, groups])

  // Overlaps are found among every run the caller can see on the same facility, not only
  // the rows on screen: picking one worklist must still show the list it competed with.
  const overlaps = useMemo(() => overlapsOf(all.filter((r) => people.has(r.person))), [all, people])
  const lanes = useMemo(() => lanesOf(rows, overlaps, window_.from, window_.to), [rows, overlaps, window_])
  const inWindow = (x: AssignmentRow) => overlapsDays(x, window_.from, window_.to)
  const sharedRows = rows.filter((r) => overlaps.get(r)?.shared.some(inWindow)).length
  /** The row's priorities over its days, clipped to the month when one is picked. */
  const prioritiesOf = (r: AssignmentRow) =>
    prioritiesIn(
      r.priorities,
      r.from > window_.from ? r.from : window_.from,
      r.to < window_.to ? r.to : window_.to,
    )

  // The dropdowns offer what the caller's data holds.
  const worklistOptions = useMemo(
    () =>
      [...names]
        .filter(([id]) => system || all.some((r) => r.worklistId === id && !r.system))
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ value: String(id), label: name })),
    [names, all, system],
  )
  const personOptions = useMemo(
    () => [...new Set(all.map((r) => r.person))].filter((p) => people.has(p)).sort().map((p) => ({ value: p, label: p })),
    [all, people],
  )
  const facilityOptions = useMemo(
    () => [...new Set(all.map((r) => r.facility))].sort().map((f) => ({ value: f, label: f })),
    [all],
  )

  /** Others on the same worklist and facility on some of the same days (in the month, when one is picked). */
  const sharedWith = (r: AssignmentRow) =>
    (overlaps.get(r)?.shared ?? []).filter(inWindow).map((x) => `${x.person} (${usDate(x.from)} – ${usDate(x.to)})`)
  /**
   * Other worklists on the same facility on some of the same days. Listed, not judged:
   * whether two lists compete for a claim depends on their payer type, service type and
   * plan setup, which the app does not have (and worklist names are not evidence of it).
   */
  const otherLists = (r: AssignmentRow) =>
    (overlaps.get(r)?.competing ?? []).filter(inWindow).map((x) => {
      const p = prioritiesOf(x)
      return `${x.worklist}: ${x.person} (${usDate(x.from)} – ${usDate(x.to)})${p ? `, priority ${priorityText(p)}` : ''}`
    })

  const columns: Column<AssignmentRow>[] = [
    { key: 'wl', header: 'Worklist', width: 280, sort: (r) => r.worklist, render: (r) => r.worklist },
    { key: 'person', header: 'Assigned', width: 180, sort: (r) => r.person, render: (r) => r.person },
    { key: 'f', header: 'Facility', width: 220, sort: (r) => r.facility, render: (r) => r.facility },
    {
      key: 'from',
      header: 'From',
      width: 96,
      sort: (r) => r.from,
      render: (r) => <span className="mono">{usDate(r.from)}</span>,
    },
    {
      key: 'to',
      header: 'To',
      width: 96,
      sort: (r) => r.to,
      title: 'The last day of the run. “→ ongoing”: the run reaches the last day of the data, so the person was still on the list then and may be still.',
      render: (r) => (
        <span className="mono">
          {usDate(r.to)}
          {/* Review #599 ("what are these arrows for?"): a bare → said nothing. */}
          {r.to === data?.to && <Ongoing last={data.to} />}
        </span>
      ),
    },
    {
      key: 'days',
      header: 'Days',
      align: 'right',
      width: 64,
      sort: (r) => daysIn(r.from, r.to),
      render: (r) => <span className="mono">{int(daysIn(r.from, r.to))}</span>,
    },
    {
      key: 'pr',
      header: 'Priority',
      align: 'right',
      width: 96,
      title:
        'The worklist’s priority over the row’s days (in the month, when one is picked), from the priority history. When two worklists match a claim, the matcher predicts the higher one. Before the history starts, today’s priority, marked “today”.',
      sort: (r) => prioritiesOf(r)?.max ?? r.priority,
      render: (r) => {
        const p = prioritiesOf(r)
        if (p)
          return (
            <span className="mono" title={p.covered ? undefined : `The history starts ${usDate(data?.priorityFrom ?? '')}: earlier days not shown`}>
              {priorityText(p)}
              {p.covered ? '' : '*'}
            </span>
          )
        return r.priority === null ? (
          <span className="mono">—</span>
        ) : (
          <span className="mono" style={{ color: 'var(--ink-3)' }} title="Today’s priority: the history does not reach these days">
            {int(r.priority)} today
          </span>
        )
      },
    },
    {
      key: 'shared',
      header: 'Same list, same days',
      width: 240,
      title:
        'Someone else on this same worklist, for this facility, on some of the same days. The model credits a claim on such a list to the first of them by email address.',
      sort: (r) => sharedWith(r).join('; ') || null,
      render: (r) => {
        const t = sharedWith(r)
        return t.length ? <span className="wa-also">⚑ {t.join('; ')}</span> : <span style={{ color: 'var(--ink-3)' }}>—</span>
      },
    },
    {
      key: 'others',
      header: 'Other lists on this facility',
      align: 'right',
      width: 110,
      title:
        'Other worklists covering this facility on some of the same days. Hover the number for them. Pick the facility above to see them all on the timeline with their priorities.',
      sort: (r) => otherLists(r).length,
      render: (r) => {
        const t = otherLists(r)
        return t.length ? (
          <span className="mono" title={t.join('\n')}>
            {int(t.length)}
          </span>
        ) : (
          <span style={{ color: 'var(--ink-3)' }}>—</span>
        )
      },
    },
  ]

  const exportColumns: ExportColumn<AssignmentRow>[] = [
    { header: 'Worklist', value: (r) => r.worklist },
    { header: 'Worklist ID', value: (r) => r.worklistId },
    { header: 'Assigned', value: (r) => r.person },
    { header: 'Facility', value: (r) => r.facility },
    { header: 'From', value: (r) => r.from },
    { header: 'To', value: (r) => r.to },
    { header: 'Ongoing', value: (r) => (r.to === data?.to ? 'Yes' : 'No') },
    { header: 'Days', value: (r) => daysIn(r.from, r.to) },
    {
      header: 'Priority',
      value: (r) => {
        const p = prioritiesOf(r)
        return p ? priorityText(p) : r.priority
      },
    },
    { header: 'Priority today', value: (r) => r.priority },
    { header: 'System list', value: (r) => (r.system ? 'Yes' : 'No') },
    { header: 'Same list, same days', value: (r) => sharedWith(r).join('; ') },
    { header: 'Other lists on this facility', value: (r) => otherLists(r).join('; ') },
  ]

  const pickedName = worklist ? names.get(Number(worklist)) : undefined

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="page-title">Worklist assignments</h1>
        <span className="pts-chip neut">one row per person × worklist × facility, from – to</span>
      </div>
      <p className="body-note" style={{ marginTop: 6, maxWidth: 900 }}>
        Who was assigned to each worklist in Medevolve, for which facilities, and on which
        days. A billed claim goes to the person assigned to its predicted worklist at the
        end of its bill month; when two worklists cover the same facility, the matcher takes
        the one with the higher priority. Claim detail’s Predicted worklist column opens
        this page at that worklist and month. Assignments from{' '}
        {data?.from ? usDate(data.from) : '3/1/2026'}
        {data?.priorityFrom
          ? `; priorities as they were on those days from ${usDate(data.priorityFrom)}, today’s before that (marked “today”)`
          : '; priorities are today’s'}
        . Priorities change often, so compare them on the same days.
      </p>

      <PopOut title="Worklist assignments" note="one row per person × worklist × facility">
        {({ button }) => (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end', marginTop: 16 }}>
              <SearchPick
                label="Worklist"
                value={worklist}
                allValue=""
                allLabel="All"
                options={worklistOptions}
                onChange={setWorklist}
                minWidth={280}
              />
              <SearchPick
                label="Person"
                value={person}
                allValue=""
                allLabel="All"
                options={personOptions}
                onChange={setPerson}
                minWidth={200}
              />
              <SearchPick
                label="Facility"
                value={facility}
                allValue=""
                allLabel="All"
                options={facilityOptions}
                onChange={setFacility}
                minWidth={220}
              />
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="eyebrow">Month</span>
                <select value={month} onChange={(e) => setMonth(e.target.value)} style={control} data-testid="assignment-month">
                  <option value="">All</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {monthYear(m)}
                    </option>
                  ))}
                  {month && !months.includes(month) && <option value={month}>{monthYear(month)}</option>}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="eyebrow">Search</span>
                <input
                  type="search"
                  value={typed}
                  placeholder="worklist, person, facility"
                  onChange={(e) => setTyped(e.target.value)}
                  style={control}
                />
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-table)' }}>
                <input type="checkbox" checked={system} onChange={(e) => setSystem(e.target.checked)} />
                Include system lists
              </label>
              <button
                type="button"
                className="reset-btn"
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  setWorklist('')
                  setPerson('')
                  setFacility('')
                  setMonth('')
                  setSystem(false)
                  setTyped('')
                  setQ('')
                }}
              >
                Clear
              </button>
            </div>

            {data && (
              <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '14px 0 0' }} data-testid="assignment-summary">
                {int(rows.length)} runs · {int(new Set(rows.map((r) => r.worklistId)).size)} worklists ·{' '}
                {int(new Set(rows.map((r) => r.person)).size)} people
                {month ? ` · ${monthYear(month)}` : ''}
                {sharedRows > 0 && ` · ⚑ ${int(sharedRows)} shared with someone else on the same list and days`}
                {hidden > 0 && ` · ${int(hidden)} more left out by the top bar’s filters`}
                {unplaced > 0 &&
                  ` · ${int(unplaced)} more on facilities no claim you can see places in a facility group`}
              </p>
            )}
            {(groupState === 'waiting' || groupState === 'no-ids') && (
              <p className="body-note" style={{ margin: '8px 0 0', color: 'var(--crit-ink)' }} data-testid="assignment-group-note">
                {groupState === 'waiting'
                  ? 'Facility group: placing each facility in its group from the claim rows…'
                  : 'Facility group is not applied here: this data build has no facility ids to place an assignment’s facility in its group.'}
              </p>
            )}

            {error && !data ? (
              <div className="card" style={{ marginTop: 10, padding: 0 }}>
                <div className="empty-note">{error}</div>
              </div>
            ) : !data || groupState === 'waiting' ? (
              <div className="card" style={{ marginTop: 10, padding: 0 }}>
                <div className="empty-note">Loading assignments…</div>
              </div>
            ) : rows.length === 0 ? (
              <div className="card" style={{ marginTop: 10, padding: 0 }}>
                <div className="empty-note" data-testid="assignment-empty">
                  {worklist
                    ? `Nobody you can see was assigned to ${pickedName ?? 'this worklist'}${month ? ` in ${monthYear(month)}` : ''}${
                        month && data.from && month < data.from.slice(0, 7)
                          ? ` — the history starts ${usDate(data.from)}`
                          : ''
                      }.`
                    : 'No assignments match these filters.'}
                </div>
              </div>
            ) : (
              <>
                <div className="card" style={{ marginTop: 10 }}>
                  <h3 className="card-heading">
                    Timeline{month ? `, ${monthYear(month)}` : ''}
                  </h3>
                  <Timeline lanes={lanes.slice(0, MAX_LANES)} total={lanes.length} from={window_.from} to={window_.to} />
                  <p className="body-note" style={{ marginTop: 8 }}>
                    One lane per person on each worklist; a bar is the days they were
                    assigned. ⚑: someone else was on the same list on some of those days.
                    Pick a facility to see every list that covered it, with its priority:
                    where two lists cover the same claims, the higher priority gets them.
                    Hover a bar for its dates.
                  </p>
                </div>
                <div style={{ marginTop: 14 }}>
                  <ExportBar filename="worklist-assignments.csv" rows={rows} columns={exportColumns} lead={button} />
                </div>
                <div className="popout-fill">
                  <div className="card" style={{ marginTop: 6, padding: 0, overflow: 'hidden' }}>
                    <DataTable
                      storageKey="worklist-assignments"
                      sortParam="sort"
                      rows={rows}
                      columns={columns}
                      rowKey={(r) => `${r.worklistId}:${r.person}:${r.facility}:${r.from}`}
                      initialSort="wl"
                      initialDir="asc"
                      // Sorting covers every row and the export has them all; drawing
                      // thousands at once is what slows a table down.
                      maxRows={1000}
                      maxHeight="60vh"
                    />
                  </div>
                </div>
                {data.to && (
                  <p className="body-note" style={{ marginTop: 8 }} data-testid="assignment-legend">
                    ↕ on a column head sorts by it. <span className="mono">→ ongoing</span> after a To
                    date: the run reaches {usDate(data.to)}, the last day of the data, so the person
                    was still on the list then and may be still.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </PopOut>
    </>
  )
}
